// Service to manage daily alert quotas based on account tier (Free vs PRO)

export const MAX_FREE_DAILY_ALERTS = 5;

/**
 * Get date string formatted as YYYY-MM-DD for daily tracking
 */
export function getTodayQuotaKey() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get current daily alert usage count
 */
export function getDailyAlertUsage() {
  const dateKey = getTodayQuotaKey();
  const storageKey = `minopro_daily_alert_count_${dateKey}`;
  try {
    const saved = localStorage.getItem(storageKey);
    const count = saved ? parseInt(saved, 10) || 0 : 0;
    return {
      date: dateKey,
      count: Math.max(0, count),
      max: MAX_FREE_DAILY_ALERTS,
    };
  } catch (e) {
    return { date: dateKey, count: 0, max: MAX_FREE_DAILY_ALERTS };
  }
}

/**
 * Check if the user is allowed to receive a new alert
 */
export function checkAlertQuota(isPro = false) {
  if (isPro) {
    return {
      allowed: true,
      count: 0,
      max: Infinity,
      remaining: Infinity,
      isPro: true,
      isLimitReached: false,
    };
  }

  const usage = getDailyAlertUsage();
  const allowed = usage.count < MAX_FREE_DAILY_ALERTS;
  return {
    allowed,
    count: usage.count,
    max: MAX_FREE_DAILY_ALERTS,
    remaining: Math.max(0, MAX_FREE_DAILY_ALERTS - usage.count),
    isPro: false,
    isLimitReached: !allowed,
  };
}

/**
 * Record a triggered alert and increment daily count for Free accounts
 */
export function recordAlertTrigger(isPro = false) {
  if (isPro) {
    return {
      allowed: true,
      count: 0,
      max: Infinity,
      remaining: Infinity,
      isPro: true,
      isLimitReached: false,
    };
  }

  const dateKey = getTodayQuotaKey();
  const storageKey = `minopro_daily_alert_count_${dateKey}`;
  const usage = getDailyAlertUsage();

  if (usage.count >= MAX_FREE_DAILY_ALERTS) {
    return {
      allowed: false,
      count: usage.count,
      max: MAX_FREE_DAILY_ALERTS,
      remaining: 0,
      isPro: false,
      isLimitReached: true,
    };
  }

  const newCount = usage.count + 1;
  try {
    localStorage.setItem(storageKey, String(newCount));
  } catch (e) {
    console.warn("Error saving alert quota:", e);
  }

  return {
    allowed: true,
    count: newCount,
    max: MAX_FREE_DAILY_ALERTS,
    remaining: Math.max(0, MAX_FREE_DAILY_ALERTS - newCount),
    isPro: false,
    isLimitReached: false,
  };
}
